import React, { useState, useEffect } from "react";
import { AiOutlineUsergroupAdd, AiOutlineSearch } from "react-icons/ai";
import OffCanvas from "../../../OffCanvas/OffCanvas";
import SearchInput from "../../../SerachInput/SearchInput";
import AddSponsor from "./AddSponsor";
import TableSponsor from "./TableSponsor";

const Sponsors = ({ userID, token, eventID }) => {
  const [isOffcanvasOpen, setIsOffcanvasOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const canvasWidth = isMobile ? "100%" : 700;

  const handleSearch = (event) => {
    setSearch(event.target.value);
  };

  const handleOpenOffCanvas = () => {
    setIsOffcanvasOpen(!isOffcanvasOpen);
  };

  return (
    <div style={styles.container}>
      {/* Header Section */}
      <div style={styles.headerSection}>
        <h1 style={styles.mainTitle}>
          Sponsors
        </h1>
        <p style={styles.subtitle}>
          Manage all event sponsors here. Add, view, and organize your sponsors
          to ensure seamless event partnerships and acknowledgments.
        </p>
      </div>

      {/* Search and Button Section */}
      <div style={styles.controlsContainer}>
        <div style={styles.searchWrapper}>
          <AiOutlineSearch style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search sponsors..."
            value={search}
            onChange={handleSearch}
            style={styles.searchInput}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={styles.clearButton}
              type="button"
            >
              ×
            </button>
          )}
        </div>

        <button
          onClick={handleOpenOffCanvas}
          style={styles.addButton}
          type="button"
        >
          <AiOutlineUsergroupAdd size={18} />
          <span>Add Sponsor</span>
        </button>
      </div>

      {/* Table Section */}
      <div style={styles.tableContainer}>
        <div style={styles.tableHeader}>
          <span style={styles.tableLabel}>Sponsor List</span>
          <span style={styles.tableCount}>
            {search ? `Searching: "${search}"` : 'All sponsors'}
          </span>
        </div>
        <div style={styles.tableWrapper}>
          <TableSponsor
            userID={userID}
            token={token}
            eventID={eventID}
            search={search}
          />
        </div>
      </div>

      {/* OffCanvas */}
      <OffCanvas
        isOffcanvasOpen={isOffcanvasOpen}
        title="Add Sponsor"
        body={
          <AddSponsor
            userID={userID}
            token={token}
            eventID={eventID}
            setIsOffcanvasOpen={setIsOffcanvasOpen}
          />
        }
        toggleSaveChanges={handleOpenOffCanvas}
        directionOffCanvas="end"
        widthOffCanvas={canvasWidth}
      />

      <style>{`
        @media (max-width: 768px) {
          .sponsors-container {
            padding: 16px 12px !important;
          }
          
          .controls-wrapper {
            flex-direction: column !important;
            gap: 12px !important;
          }
          
          .search-wrapper-mobile {
            width: 100% !important;
          }
          
          .add-btn-mobile {
            width: 100% !important;
            height: 48px !important;
          }
        }

        @media (max-width: 480px) {
          .main-title {
            font-size: 20px !important;
          }
          
          .subtitle {
            font-size: 13px !important;
          }
          
          .search-input-mobile {
            font-size: 16px !important;
            height: 46px !important;
          }
        }
      `}</style>
    </div>
  );
};

// Professional Styles
const styles = {
  container: {
    width: '100%',
    maxWidth: '100%',
    padding: '24px 20px',
    boxSizing: 'border-box',
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
  },
  headerSection: {
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '2px solid #e9ecef',
  },
  mainTitle: {
    fontSize: '26px',
    fontWeight: '700',
    color: '#111827',
    margin: '0 0 8px 0',
    letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0,
    lineHeight: '1.6',
    maxWidth: '800px',
  },
  controlsContainer: {
    display: 'flex',
    gap: '12px',
    alignItems: 'stretch',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  searchWrapper: {
    flex: '1',
    minWidth: '250px',
    position: 'relative',
    maxWidth: '500px',
  },
  searchIcon: {
    position: 'absolute',
    left: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9ca3af',
    fontSize: '20px',
    pointerEvents: 'none',
    zIndex: 2,
  },
  searchInput: {
    width: '100%',
    padding: '12px 44px 12px 48px',
    border: '2px solid #e5e7eb',
    borderRadius: '10px',
    fontSize: '15px',
    backgroundColor: '#ffffff',
    color: '#1f2937',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease',
    outline: 'none',
    height: '48px',
  },
  clearButton: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: '#f3f4f6',
    border: 'none',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    color: '#6b7280',
    fontSize: '20px',
    lineHeight: '1',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
    transition: 'all 0.15s ease',
  },
  addButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #f68a20 0%, #e57a10 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(246, 138, 32, 0.3)',
    height: '48px',
    flexShrink: 0,
  },
  tableContainer: {
    width: '100%',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
  },
  tableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
    borderBottom: '2px solid #e5e7eb',
    flexWrap: 'wrap',
    gap: '8px',
  },
  tableLabel: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#111827',
    letterSpacing: '0.3px',
  },
  tableCount: {
    fontSize: '13px',
    color: '#6b7280',
    fontWeight: '500',
  },
  tableWrapper: {
    width: '100%',
    overflowX: 'auto',
  },
};
export default Sponsors;